import React, { useState, useEffect, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button, Col as ColStrap, Row as RowStrap } from 'reactstrap';
import { Field, reduxForm, InjectedFormProps, formValueSelector, getFormValues } from 'redux-form';
import { useLocation } from 'react-router-dom';

import Card, { CardBody } from '../../../components/Card';
import { getTranslationKey } from '../../../modules/utils';
import { useCountriesList } from '../../../hooks/useCountriesList';
import { IDictionary } from '../../../modules/types';
import {
  fetchPaymentInfoAction,
  showNotifyFail,
  updateBillingAddress,
  updateUserTaxAction
} from '../../../modules/actions';
import { getPaymentInfo } from '../../../modules/selectors/paymentInfo';
import { getUser } from '../../../modules/selectors';
import InputGroup from '../../../elements/Forms/InputGroup';
import Select from '../../../elements/Forms/Select';
import { InputItem } from '../../../components';
import { isEqual } from 'lodash';
import { useStateChange } from '../../../hooks/useStateChange';
import styled from 'styled-components';
import classNames from 'classnames';
import {
  Ifields,
  getValidate,
  useFormFields,
  getCountriesOptions,
  seperateStreetField
} from '../helpers/billing-address-helpers';
import { media } from '../../../assets/css/media';

const Col = styled(ColStrap)`
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
`;

const Row = styled(RowStrap)`
  margin-left: -0.5rem !important;
  margin-right: -0.5rem !important;
`;

const StyledInputItem = styled(InputItem)`
  &&&& {
    .input-group-text {
      border-color: rgb(204, 204, 204);
    }

    label {
      font-weight: 400;
    }
  }
`;

const ButtonSection = styled.div`
  display: flex;
  @media all and (max-width: ${media.mobile}) {
    flex-direction: column;
    button {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
`;

export type SubmitButton = (props: { onClick?: () => void; disabled?: boolean }) => JSX.Element;

interface IProps {
  saveOnBlur?: boolean;
  showVatId?: boolean;
  showTitle?: boolean;
  settingsTab?: boolean;
  country?: string;
  isSubmit?: boolean;
  setIsSubmit?: (isSubmit: boolean) => void;
  setVatIdHasUnsavedChanges: (vatIdHasUnsavedChanges: boolean) => void;
  setBillingAddressHasUnsavedChanges: (billingAddressHasUnsavedChanges: boolean) => void;
  submitButton?: SubmitButton;
  onCancel?: () => void;
}

const BillingAddressFormUnwrapped: React.FunctionComponent<IProps & InjectedFormProps> = ({
  handleSubmit,
  initialize,
  showVatId,
  country,
  showTitle = true,
  settingsTab,
  isSubmit,
  saveOnBlur = true,
  setIsSubmit,
  invalid,
  setVatIdHasUnsavedChanges,
  setBillingAddressHasUnsavedChanges,
  submitButton: SubmitButton,
  onCancel
}) => {
  const dispatch = useDispatch();
  const countriesList = useCountriesList();
  const countriesOptions = getCountriesOptions(countriesList);
  const userAuth = useSelector(getUser);
  const formValues = useSelector(getFormValues('billindAddressForm')) as
    | IDictionary<string>
    | undefined;
  const [vatId, setVatId] = useState(!userAuth.isUpdating && userAuth.taxSettings?.vatId);
  const { emailField, companyField, col6Fields, ...fields } = useFormFields(countriesList);
  const location = useLocation();
  const {
    address: billingaddress = {
      city: '',
      company: '',
      country: '',
      email: '',
      firstName: '',
      lastName: '',
      street: '',
      zipcode: '',
      streetNumber: ''
    },
    updatingAddress = false,
    userId: paymentInfoUserId
  } = useSelector(getPaymentInfo);
  const paymentInfoIsLoaded = !!paymentInfoUserId;

  const countryIsEuOrCHNotGermany =
    (countriesList.find((c) => c.id === country)?.isEU && country !== 'germany') ||
    country === 'switzerland';

  // submit functions
  const submitVatId = (vatId: string, waitForBillingAddressUpdate: boolean = false) => {
    dispatch(
      updateUserTaxAction({
        isEdit: true,
        isPaymentPage: true,
        waitForBillingAddressUpdate,
        form: { ...userAuth.taxSettings, vatId },
        closeForm: onCancel
      })
    );
  };

  const handleVatIdChange = (vatId: string) => {
    if (vatId === userAuth.taxSettings.vatId) {
      setVatIdHasUnsavedChanges(false);
    } else {
      setVatIdHasUnsavedChanges(true);
    }
    setVatId(vatId);
  };

  const vatIdTimerRef = useRef(null);
  const updateVatId = (vatId: string, timeout: number = 2000) => {
    handleVatIdChange(vatId);
    clearTimeout(vatIdTimerRef.current);

    if (typeof vatId === 'string') {
      if (timeout === 0) {
        submitVatId(vatId);
      } else {
        vatIdTimerRef.current = setTimeout(() => {
          submitVatId(vatId);
        }, timeout);
      }
    }
  };

  const getSubmitAddress =
    (updateTax: boolean = true) =>
    (values: IDictionary<string>) => {
      try {
        const isBillingAddressChanged = !isEqual(values, billingaddress);
        const shouldUpdateVatId =
          updateTax &&
          showVatId &&
          countryIsEuOrCHNotGermany &&
          vatId !== userAuth.taxSettings.vatId;

        if (isBillingAddressChanged) {
          dispatch(
            updateBillingAddress({
              email: values.email,
              country: values.country,
              firstName: values.firstName,
              lastName: values.lastName,
              company: values.company,
              street: values.street,
              streetNumber: values.streetNumber,
              zipcode: values.zipcode,
              city: values.city,
              routerPath: location.pathname,
              justUpdate: true,
              closeForm: !shouldUpdateVatId ? onCancel : undefined,
              silentSuccess: shouldUpdateVatId
            })
          );
        } else {
          setBillingAddressHasUnsavedChanges(false);
        }

        if (shouldUpdateVatId) {
          // Submit VAT ID immediately, saga will wait for billing address if needed
          submitVatId(vatId, isBillingAddressChanged);
        } else {
          dispatch({
            type: 'SET_USER_TAX_SETTINGS',
            payload: {
              ...userAuth.taxSettings,
              vatIdIsValid: true
            }
          });
        }

        if (!countryIsEuOrCHNotGermany && settingsTab) {
          submitVatId('', false);
        }
      } catch (e) {
        dispatch(showNotifyFail());
      }
    };

  const submit = (isSubmitFromPaymentElement = false) => {
    if (invalid && !isSubmitFromPaymentElement) return;
    handleSubmit(getSubmitAddress())();
  };

  const submitAddressWithoutTax = getSubmitAddress(false);
  const submitWithoutTax = () => {
    if (invalid) return;
    handleSubmit(submitAddressWithoutTax)();
  };

  // Fetch payment info
  useEffect(() => {
    if (!paymentInfoIsLoaded) {
      dispatch(fetchPaymentInfoAction());
    }
  }, [paymentInfoIsLoaded]);

  // initialize form
  useEffect(() => {
    initialize({
      company: billingaddress.company || userAuth.company,
      email: billingaddress.email || userAuth.email,
      country: billingaddress.country || userAuth._country,
      city: billingaddress.city || userAuth.city,
      firstName: billingaddress.firstName || userAuth.first_name,
      lastName: billingaddress.lastName || userAuth.last_name,
      street: billingaddress.street || seperateStreetField(userAuth.street).street,
      streetNumber:
        billingaddress.streetNumber || seperateStreetField(userAuth.street).streetNumber,
      zipcode: billingaddress.zipcode || userAuth.zipcode
    });
  }, [
    billingaddress.company,
    billingaddress.email,
    billingaddress.country,
    billingaddress.city,
    billingaddress.firstName,
    billingaddress.lastName,
    billingaddress.street,
    billingaddress.streetNumber,
    billingaddress.zipcode,
    userAuth.company,
    userAuth.email,
    userAuth._country,
    userAuth.city,
    userAuth.first_name,
    userAuth.last_name,
    userAuth.street,
    userAuth.zipcode
  ]); // eslint-disable-line

  useEffect(() => {
    if (!vatId && !userAuth.isUpdating) {
      setVatId(userAuth.taxSettings.vatId);
    }
  }, [userAuth]);

  // if the form is invalid we are not ready to submit
  const validateVatId = showVatId && countryIsEuOrCHNotGermany;

  useEffect(() => {
    const hasVatIdError = validateVatId && userAuth.taxSettings.vatIdIsValid === false;
    const hasError = invalid || userAuth.taxSettings.errorMessage || hasVatIdError;

    if (hasError && isSubmit) {
      setIsSubmit(false);
    }
  }, [
    invalid,
    userAuth.taxSettings.errorMessage,
    userAuth.taxSettings.vatIdIsValid,
    isSubmit,
    validateVatId
  ]);

  // if a user has clicked on confirm button we should submit the form
  useStateChange(isSubmit, (isSubmitPrev, isSubmit) => {
    const isSubmitChanged = isSubmitPrev !== isSubmit;

    if (isSubmitChanged && isSubmit) {
      submit(true);
    }
  });

  // if the country has changed we should submit the form without tax
  useStateChange(country, (countryPrev, country) => {
    if (!SubmitButton && countryPrev !== country && formValues) {
      // Bypass redux-form's invalid gate: country-specific zipcode length checks
      // (createValidateZip) can mark the form invalid the instant country changes
      // (e.g. DE→AT with a 5-digit zip), which would otherwise block VAT recalc.
      submitAddressWithoutTax(formValues);
    }
  });

  // if the user has changed the form we should set the unsaved changes flag
  // and set if back to false when form is submitted.
  const onFieldChange = () => {
    setBillingAddressHasUnsavedChanges(true);
  };
  useStateChange(userAuth.isUpdating, (isUpdatingPrev, isUpdating) => {
    if (isUpdatingPrev && !isUpdating) {
      setVatIdHasUnsavedChanges(false);
    }
  });
  useStateChange(updatingAddress, (updatingAddressPrev, updatingAddress) => {
    if (updatingAddressPrev && !updatingAddress) {
      setBillingAddressHasUnsavedChanges(false);
    }
  });

  const countryField = {
    field: 'country',
    options: countriesOptions,
    title: `${getTranslationKey('shop.taxConfig.locationLabel')}*`
  };

  const renderField = (field: Ifields) => (
    <Field
      component={InputGroup}
      name={field.field}
      validate={field.validators}
      field={field}
      normalize={(value: any) => (/^\s/.test(value) ? value.trim() : value)}
      onBlur={saveOnBlur ? submitWithoutTax : null}
      onChange={onFieldChange}
      disabled={updatingAddress}
    />
  );

  return (
    <form onSubmit={() => submit()}>
      {showTitle && (
        <>
          <i data-cy="billingAddressModalTitle" className="nav-icon icon-user mr-1" />{' '}
          {getTranslationKey('billingAddressModalTitle')}
        </>
      )}
      <div data-cy="fadeIn" className="animated fadeIn">
        <Row>
          <Col data-cy="fields" sm="12" md="12" lg="12" className="p-0">
            <Card
              className="p-0"
              style={{ paddingBottom: '0', paddingTop: '0', marginBottom: '0' }}
            >
              <CardBody>
                <Row>
                  <Col xs="6">{renderField(fields.firstNameField)}</Col>
                  <Col xs="6">{renderField(fields.lastNameField)}</Col>
                </Row>

                <Row data-cy="emailFieldRow">
                  <Col data-cy="emailField" xs="12" lg="12">
                    <Field
                      component={InputGroup}
                      name={emailField.field}
                      validate={getValidate(emailField.rules)}
                      field={emailField}
                      normalize={(value: any) => (/^\s/.test(value) ? value.trim() : value)}
                      onBlur={saveOnBlur ? submitWithoutTax : null}
                      onChange={onFieldChange}
                      disabled={updatingAddress}
                    />
                  </Col>
                </Row>

                <Row data-cy="companyFieldRow">
                  <Col data-cy="companyField" lg={12}>
                    <Field
                      component={InputGroup}
                      name={companyField.field}
                      validate={getValidate(companyField.rules)}
                      field={companyField}
                      normalize={(value: any) => (/^\s/.test(value) ? value.trim() : value)}
                      onBlur={saveOnBlur ? submitWithoutTax : null}
                      onChange={onFieldChange}
                      disabled={updatingAddress}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" md="8">
                    {renderField(fields.streetField)}
                  </Col>
                  <Col xs="12" md="4">
                    {renderField(fields.streetNumberField)}
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" md="4">
                    {renderField(fields.zipcodeField)}
                  </Col>
                  <Col xs="12" md="8">
                    {renderField(fields.cityField)}
                  </Col>
                </Row>
                <Row data-cy="countryFieldRow">
                  <Col data-cy="countryField" lg={12}>
                    <Field
                      component={Select}
                      name={countryField.field}
                      field={countryField}
                      onChange={onFieldChange}
                      disabled={updatingAddress}
                    />
                  </Col>
                </Row>
                {validateVatId && (
                  <Row data-cy="vatFieldRow">
                    <Col data-cy="vatField" lg={12}>
                      <StyledInputItem
                        id="vat-id"
                        withTooltip
                        tooltipText={getTranslationKey('settingsSection.tax.vatIDTooltip')}
                        label={getTranslationKey('settingsSection.tax.vatIDLabel')}
                        type="text"
                        changeCallback={settingsTab ? handleVatIdChange : updateVatId}
                        value={vatId}
                        placeholder="-"
                        onBlurCallback={saveOnBlur ? () => updateVatId(vatId, 2000) : undefined}
                        errorText={userAuth.taxSettings.errorMessage}
                      />
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <ButtonSection>
        {SubmitButton && (
          <SubmitButton
            onClick={() => submit(true)}
            disabled={updatingAddress || !userAuth._id || !paymentInfoUserId || userAuth.isUpdating}
          />
        )}
        {onCancel && (
          <Button
            color="secondary"
            className={classNames('px-5 mt-3 text-uppercase', { 'ml-2': !!SubmitButton })}
            type="button"
            onClick={onCancel}
          >
            {getTranslationKey('messages.cancel')}
          </Button>
        )}
      </ButtonSection>
    </form>
  );
};

const selector = formValueSelector('billindAddressForm');
const mapStateToProps = (state: any) => {
  const country = selector(state, 'country');

  return {
    country
  };
};

export const BillingAddressForm = connect(mapStateToProps)(
  reduxForm<IProps, any>({
    form: 'billindAddressForm',
    destroyOnUnmount: false,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true
  })(BillingAddressFormUnwrapped)
);
